/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { CellStyle, ExcelStyle, ICellRendererParams, IRowNode, ValueFormatterParams } from 'ag-grid-community';
import { getFlagColor } from 'jotai/flagColor';
import { FlagType, TireAgeMetadata } from 'models/viewModels';
import { qtmBlack } from 'theme/palettes/Palette';

const decimalFormatter = (params: ValueFormatterParams, fractionDigits?: number) => {
  const n = Number(params.value);
  return isNaN(n) ? '' : n.toFixed(fractionDigits);
};

export const threeDecimalValueFormatter = (params: ValueFormatterParams) => decimalFormatter(params, 3);

export const twoDecimalValueFormatter = (params: ValueFormatterParams) => decimalFormatter(params, 2);

export const TEXT_WIDTH = 75;
export const TWO_DIGIT_WIDTH = 35;
export const THREE_DIGIT_WIDTH = 40;
export const FOUR_DIGIT_WIDTH = 50;
export const FIVE_DIGIT_WIDTH = 59;
export const SIX_DIGIT_WIDTH = 70;
export const SESSION_TIME_WIDTH = 80;

// Used for vehicle number
export const numberAsStringComparator = (valueA: string, valueB: string) => parseInt(valueA) - parseInt(valueB);

export const dateValueFormatter = (params: ValueFormatterParams) => new Date(params.value).toLocaleDateString();

export const booleanCellRenderer = (props: ICellRendererParams) =>
  props.value === null || props.value === undefined ? '-' : props.value ? 'True' : 'False';

// Used when data not yet populated in chart
export const incompleteCellRenderer = (props: ICellRendererParams) => {
  return props.value === undefined || props.value === null ? '-' : props.value;
};

export const deltaTargetCellRenderer = (props: ICellRendererParams) => {
  return props.value === undefined || props.value === null || props.value === 0 ? '-' : props.value;
};

export const flagCellRenderer = (props: ICellRendererParams) => {
  return props.value === undefined || props.value === null || props.value === 'Unknownflagtype' ? '-' : props.value;
};

export const deltaValueFormatter = (params: ValueFormatterParams) =>
  params.data.pitStatus === true
    ? 'PIT'
    : params.data.inGarage === true
    ? 'OFF'
    : params.data.isInactive === true
    ? 'DNF'
    : params.data.deltaToTarget > 200000 // Hide large deltas
    ? '-'
    : `${params.data.deltaToTarget > 0 ? '-' : ''}${
        params.data.lapsToTarget > 0
          ? `${params.data.deltaToTarget.toFixed(1)}(${params.data.lapsToTarget})`
          : `${params.data.deltaToTarget.toFixed(3)}`
      } `;

export const pointsGapToLeaderValueFormatter = (params: ValueFormatterParams) =>
  `${params.data.gapToLeader > 0 ? '-' : ''}${params.data.gapToLeader.toFixed(3)}`;

export const gapToReferenceValueFormatter = (params: ValueFormatterParams) => {
  const referenceVehicleNumber: string = params.data.referenceVehicle;
  if (!referenceVehicleNumber) {
    return '-';
  }
  const data = params.data.gapmetadataList.find((item: any) => item.vehicleNumber === referenceVehicleNumber);
  return data ? (data.gap * -1).toFixed(3) : '-';
};

export const ppLapValueFormatter = (params: ValueFormatterParams) => {
  return params.data.ppLap > 0 ? params.data.ppLap : '';
};

export const lsLapsFormatter = (params: ValueFormatterParams): string => {
  const tireAgeMetadata: TireAgeMetadata = params.data.tireAgeMetadata;

  if (tireAgeMetadata == null) {
    return '';
  }

  const { lsLaps } = tireAgeMetadata;
  return `${lsLaps}`;
};

export const rsLapsFormatter = (params: ValueFormatterParams): string => {
  const tireAgeMetadata: TireAgeMetadata = params.data.tireAgeMetadata;

  if (tireAgeMetadata == null) {
    return '';
  }

  const { rsLaps } = tireAgeMetadata;
  return `${rsLaps}`;
};

export const pitStatusCellRenderer = (props: ICellRendererParams) => {
  return props.value ? 'In Pit' : '-';
};

export const deltaComparator = (_valueA: number, _valueB: number, nodeA: IRowNode<any>, nodeB: IRowNode<any>) =>
  nodeA.data.runningPosition > nodeB.data.runningPosition ? 1 : -1;

// Because empty columns are ugly
export const bestLapFormatter = (params: ValueFormatterParams) => (params.data.bestLap > 0 ? params.data.bestLap : '-');

export const bestLapComparator = (_: number, __: number, nodeA: IRowNode<any>, nodeB: IRowNode<any>) =>
  nodeA.data.bestLap - nodeB.data.bestLap;

export const bestTimeComparator = (valueA: number, valueB: number, nodeA: IRowNode<any>, nodeB: IRowNode<any>) => {
  if (valueA === 0 || nodeA.data.lapsCompleted === 0) {
    return 1;
  }
  if (valueB === 0 || nodeB.data.lapsCompleted === 0) {
    return -1;
  }
  return valueA - valueB;
};

export const stringToNumComparator = (valueA: string, valueB: string) => {
  const numA = parseFloat(valueA);
  const numB = parseFloat(valueB);

  return isNaN(numA) ? 1 : isNaN(numB) ? -1 : numA - numB;
};

// Used when data not yet populated in table
export const invalidPositionRenderer = (props: ICellRendererParams) => {
  return props.value === 999 || props.data.isDatabaseRecord ? '-' : props.value;
};

// Used when data not yet populated in table
export const invalidQualPositionRenderer = (props: ICellRendererParams) => {
  return props.value === 999 ? '-' : props.value;
};

export const defaultExcelStyles: ExcelStyle[] = [
  {
    id: 'numberType',
    numberFormat: {
      format: '0.000',
    },
  },
  {
    id: 'stringType',
    dataType: 'String',
  },
];

export const defaultCellStyle: CellStyle = {
  textAlign: 'right',
};

export const cellStyleError: CellStyle = {
  backgroundColor: '#FFE8E8',
  color: 'black',
};

export const cellStyleInvalid: CellStyle = {
  fontWeight: 'bold',
};

export const unverifiedCellStyle: CellStyle = {
  textAlign: 'right',
  //prevents the italics making the text cut
  paddingRight: '2px',
  fontStyle: 'italic',
};

export const flagCellClass = (flagType?: number) => {
  const flag = getFlagColor(flagType !== undefined ? flagType : FlagType.UNKNOWNFLAGTYPE).flag;

  switch (flag) {
    case FlagType.GREEN:
      return 'greenFlag';
    case FlagType.RED:
      return 'redFlag';
    case FlagType.YELLOW:
      return 'yellowFlag';
    default:
      return 'unknownFlag';
  }
};

export const flagCellStyle = (flagType?: number) => {
  return {
    background: getFlagColor(flagType !== undefined ? flagType : FlagType.UNKNOWNFLAGTYPE).fillHex,
    color: qtmBlack,
  };
};
